// Registrering av arbetsberedning som upprättats utanför systemet (entreprenörens egen mall,
// papper eller annat system). Bas-U noterar att den finns, vilken vecka och var originalet är.
import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  folderId: string | null;
  onCreated: (id: string) => void;
}

function currentWeek(): number {
  const d = new Date();
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const firstDayNr = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNr + 3);
  return 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * 86400000));
}

const ExternalWorkPrepDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  projectId,
  folderId,
  onCreated,
}) => {
  const [name, setName] = useState("");
  const [moment, setMoment] = useState("");
  const [week, setWeek] = useState<number>(currentWeek());
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [contractor, setContractor] = useState("");
  const [location, setLocation] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error("Ange ett namn på beredningen");
    if (!folderId) return toast.error("Ingen mapp för arbetsberedningar kunde hittas");
    setSaving(true);
    const { data, error } = await supabase
      .from("work_preparations")
      .insert({
        project_id: projectId,
        folder_id: folderId,
        name: name.trim(),
        status: "utkast",
        review_status: "utkast",
        origin: "extern",
        external_week: week || null,
        external_year: year || null,
        external_contractor: contractor.trim() || null,
        external_location: location.trim() || null,
        data: {
          fields: {
            arbetsmoment: moment.trim(),
            gjordAv: contractor.trim(),
            startdatum: start || "",
            slutdatum: end || "",
          },
          external: {
            genomgangenMedArbetslaget: reviewed,
            kommentar: comment.trim(),
          },
        },
      } as any)
      .select("id")
      .single();
    setSaving(false);
    if (error || !data) return toast.error("Kunde inte registrera beredningen");
    toast.success("Extern arbetsberedning registrerad");
    onOpenChange(false);
    setName("");
    setMoment("");
    setContractor("");
    setLocation("");
    setComment("");
    setReviewed(false);
    onCreated(data.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Registrera extern arbetsberedning</DialogTitle>
          <DialogDescription>
            För beredningar som upprättats någon annanstans. Du noterar att den finns – och kan
            ladda upp och AI-granska dokumentet efteråt.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="ewp-name">Namn på beredningen *</Label>
            <Input
              id="ewp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="t.ex. Montage prefab vägg plan 3"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="ewp-moment">Arbetsmoment</Label>
            <Input
              id="ewp-moment"
              value={moment}
              onChange={(e) => setMoment(e.target.value)}
              placeholder="Vad omfattar beredningen?"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ewp-week">Vecka</Label>
              <Input
                id="ewp-week"
                type="number"
                min={1}
                max={53}
                value={week}
                onChange={(e) => setWeek(Number(e.target.value))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ewp-year">År</Label>
              <Input
                id="ewp-year"
                type="number"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ewp-start">Startdatum</Label>
              <Input id="ewp-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ewp-end">Slutdatum</Label>
              <Input id="ewp-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="ewp-contractor">Entreprenör / upprättad av</Label>
            <Input
              id="ewp-contractor"
              value={contractor}
              onChange={(e) => setContractor(e.target.value)}
              placeholder="Företag eller person"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="ewp-location">Var finns originalet?</Label>
            <Input
              id="ewp-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="t.ex. Entreprenörens pärm på plats, Dalux, mejl"
            />
          </div>

          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={reviewed} onCheckedChange={(v) => setReviewed(v === true)} />
            <span>Beredningen är genomgången med arbetslaget</span>
          </label>

          <div className="space-y-1">
            <Label htmlFor="ewp-comment">Kommentar</Label>
            <Textarea
              id="ewp-comment"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Sparar…" : "Registrera"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExternalWorkPrepDialog;
